import { useState, useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { GoogleLogin } from '@react-oauth/google'
import { useAuthStore } from '@/stores/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { toast } from '@/components/ui/toaster'
import logoSrc from '@/assets/logo.svg'

export function RegisterPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { register, googleLogin, isLoading } = useAuthStore()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Persist product_url from landing page to localStorage
  useEffect(() => {
    const productUrl = searchParams.get('product_url')
    if (productUrl) {
      localStorage.setItem('pending_product_url', productUrl)
    }
  }, [searchParams])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (password.length < 8) {
      toast({
        title: 'Password too short',
        description: 'Password must be at least 8 characters.',
        variant: 'destructive',
      })
      return
    }

    if (password !== confirmPassword) {
      toast({
        title: 'Passwords do not match',
        description: 'Please make sure your passwords match.',
        variant: 'destructive',
      })
      return
    }

    try {
      const result = await register(email, password)

      if (result.verificationRequired) {
        // Redirect to email verification page
        toast({
          title: 'Verification required',
          description: 'Please check your email for a verification code.',
        })
        navigate(`/verify-email?email=${encodeURIComponent(email)}`)
      } else {
        // Mock mode - directly authenticated
        navigate('/')
      }
    } catch (error) {
      // Extract error message from API response
      let errorMessage = 'Please try again later.'

      if (error && typeof error === 'object' && 'response' in error) {
        const axiosError = error as { response?: { data?: { error?: { message?: string }; detail?: string } } }
        if (axiosError.response?.data?.error?.message) {
          errorMessage = axiosError.response.data.error.message
        } else if (axiosError.response?.data?.detail) {
          errorMessage = axiosError.response.data.detail
        }
      }

      toast({
        title: 'Registration failed',
        description: errorMessage,
        variant: 'destructive',
      })
    }
  }

  const handleGoogleSuccess = async (response: { credential?: string }) => {
    if (!response.credential) {
      toast({
        title: 'Google sign up failed',
        description: 'No credential received from Google.',
        variant: 'destructive',
      })
      return
    }

    try {
      await googleLogin(response.credential)
      navigate('/')
    } catch {
      toast({
        title: 'Google sign up failed',
        description: 'Please try again.',
        variant: 'destructive',
      })
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex items-center justify-center mb-2">
            <img src={logoSrc} alt="LinkToCreative" className="h-[5.5rem] object-contain" />
          </div>
          <h1 className="text-3xl font-bold font-brand tracking-tight text-gray-900">LinkToCreative</h1>
          <p className="mt-2 text-gray-600">Create your account</p>
        </div>

        <div className="mt-8 bg-white p-8 rounded-xl border border-gray-200/60 shadow-sm space-y-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Email address
              </label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1"
                placeholder="you@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-1"
                placeholder="Create a password"
              />
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm Password
              </label>
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                className="mt-1"
                placeholder="Confirm your password"
              />
            </div>

            <Button type="submit" className="w-full" disabled={isLoading}>
              {isLoading ? 'Creating account...' : 'Create account'}
            </Button>
          </form>

          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-gray-500">Or continue with</span>
            </div>
          </div>

          <div className="flex justify-center">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => {
                toast({
                  title: 'Google sign up failed',
                  description: 'Please try again.',
                  variant: 'destructive',
                })
              }}
              size="large"
              width="100%"
              text="signup_with"
            />
          </div>

          <p className="text-center text-sm text-gray-600">
            Already have an account?{' '}
            <Link to="/login" className="text-primary hover:underline">
              Sign in
            </Link>
          </p>
        </div>

        <p className="text-center text-xs text-gray-500">
          By signing up, you get 5 free credits to try the platform.
        </p>
      </div>
    </div>
  )
}
